import { useCallback, useEffect, useRef } from 'react';
import { useInlineAssistant } from '../../inline/useInlineAssistant';

export interface UseAIGenerationOptions {
  value: string;
  onChange: (value: string) => void;
  origin: string;
  systemPrompt?: string;
  onComplete?: (text: string) => void;
  onError?: (error: Error) => void;
  onDelta?: (delta: string) => void;
  disabled?: boolean;
  autoGenerate?: boolean;
  streaming?: boolean;
  getUserPrompt?: (textInput: string) => string;
}

export function useAIGeneration({
  value,
  onChange,
  origin,
  systemPrompt,
  onComplete,
  onError,
  onDelta,
  disabled = false,
  autoGenerate = false,
  streaming = false,
  getUserPrompt,
}: UseAIGenerationOptions) {
  const { generate, isGenerating, content, error, reset } = useInlineAssistant();
  const hasAutoGenerated = useRef(false);
  const valueBeforeGeneration = useRef(value);
  const getUserPromptRef = useRef(getUserPrompt);
  getUserPromptRef.current = getUserPrompt;

  const displayValue = isGenerating ? (streaming ? content : '') : value;
  const resolvedPrompt = getUserPrompt ? getUserPrompt(value.trim()) : value.trim();
  const isSendDisabled = !resolvedPrompt || disabled || isGenerating;

  const doGenerate = useCallback(
    (prompt: string) => {
      valueBeforeGeneration.current = value;
      onChange('');
      reset();

      generate({
        prompt,
        origin,
        systemPrompt,
        onDelta,
        onComplete: (text) => {
          onChange(text);
          onComplete?.(text);
        },
        onError: (err) => {
          onChange(valueBeforeGeneration.current);
          onError?.(err);
        },
      });
    },
    [value, onChange, origin, systemPrompt, onDelta, onComplete, onError, generate, reset]
  );

  useEffect(() => {
    if (autoGenerate && systemPrompt && !value && !hasAutoGenerated.current && !disabled) {
      hasAutoGenerated.current = true;
      doGenerate(systemPrompt);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = useCallback(() => {
    const fn = getUserPromptRef.current;
    const prompt = fn ? fn(value.trim()) : value.trim();
    if (!prompt || disabled || isGenerating) {
      return;
    }
    doGenerate(prompt);
  }, [value, disabled, isGenerating, doGenerate]);

  return {
    displayValue,
    isSendDisabled,
    handleGenerate,
    isGenerating,
    error,
  };
}
